import React, { useCallback, useEffect, useState } from "react";
import SectionTitle from "../../Components/SectionTitle";
import { BettingWebsitesColumns, BettingWebsiteTableType } from "./types";
import { getCounter } from "../../lib/functions";
import { Button, Drawer, Image, Input, message, Table } from "antd";
import { deleteWebsiteByID, getBettingWebsites } from "./action";
import { Link, useNavigate } from "react-router-dom";
import { PiPlus } from "react-icons/pi";
import DashPagination from "../../Components/DashPagination";
import DashModal from "../../Components/DashModal";
import AddNewBettingWebsite from "./Components/AddNewBettingWebsite";
import EditRatings from "./Components/EditRatings";
import EditContacts from "./Components/EditContacts";
import EditLanguages from "./Components/EditLanguages";
import EditPaymentGateways from "./Components/EditPaymentGateways";
import EditFeatures from "./Components/EditFeatures";
import GeneralEdit from "./Components/Popovers/GeneralEdit";
import EditTitle from "./Components/EditTitle";
import EditShortDescription from "./Components/EditShortDescription";
import EditLongDescription from "./Components/EditLongDescription";
import { EditQAndAForm } from "./Components/EditQAndAForm";
import EditBettingWebsiteImage from "./Components/EditBettingWebsitesImage";
import { CLOUD_URL } from "../../lib/env";
const { Search } = Input;

type Props = {
  addNewWebsite?: boolean;
  editRatings?: boolean;
  editContacts?: boolean;
  editLanguages?: boolean;
  editGateways?: boolean;
  editFeatures?: boolean;
  edit?: boolean;
  editTitle?: boolean;
  editShortDescription?: boolean;
  editLongDescription?: boolean;
  editQAndA?: boolean;
  editImage?: boolean;
};

const BettingWebsites = (props: Props) => {
  const router = useNavigate();

  const [data, setData] = useState<BettingWebsiteTableType[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpand = (expanded: boolean, record: BettingWebsiteTableType) => {
    setExpandedRowKeys(expanded ? [record.id] : []);
  };
  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("BettingWebsite");
      setTotal(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const websites = await getBettingWebsites(pageSize, page, query);
      const formatted = websites.map((wb) => ({
        id: wb.id,
        en: wb.MainInfo.Title.En,
        ar: wb.MainInfo.Title.Ar,
        fr: wb.MainInfo.Title.Fr,
        rates: wb.Ratings,
        contacts: wb.Contacts,
        languages: wb.Languages,
        paymentgateways: wb.PaymentGateways,
        features: wb.Features,
        image: wb.MainInfo.Image,
      }));
      setData(formatted);
    } catch (error) {
      message.error("Failed to fetch websites");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    try {
      await deleteWebsiteByID(id);
      message.success("Website deleted");
      fetchData();
      fetchCount();
    } catch (error) {
      message.error("Failed to delete feature");
    }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Betting Websites" />
      <div className="flex justify-between items-center">
        <Link
          to={`/betting-websites/add?page=${page + 1}&pageSize=${pageSize}`}
        >
          <Button type="primary" icon={<PiPlus />}>
            Add New Betting Website
          </Button>
        </Link>
        <Search
          allowClear
          placeholder="Search website..."
          className="w-1/5"
          onSearch={(value) => setQuery(value)}
        />
      </div>

      <Table<BettingWebsiteTableType>
        rowKey="id"
        columns={BettingWebsitesColumns(page, pageSize, handleDelete)}
        dataSource={data}
        loading={loading}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <Image width={200} src={CLOUD_URL + "/" + record.image} />
          ),
          rowExpandable: () => true,
          onExpand: handleExpand,
          expandedRowKeys,
        }}
      />
      <DashPagination
        page={page}
        setPage={setPage}
        totalNumber={total}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      {props.addNewWebsite && (
        <DashModal
          title="Add New Betting Website"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <AddNewBettingWebsite fetchData={fetchData} />
        </DashModal>
      )}

      {props.editRatings && (
        <DashModal
          title="Edit Ratings"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditRatings fetchData={fetchData} />
        </DashModal>
      )}

      {props.editContacts && (
        <DashModal
          title="Edit Contacts"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditContacts fetchData={fetchData} />
        </DashModal>
      )}

      {props.editLanguages && (
        <DashModal
          title="Edit Languages"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditLanguages fetchData={fetchData} />
        </DashModal>
      )}

      {props.editGateways && (
        <DashModal
          title="Edit Payment Gateways"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditPaymentGateways fetchData={fetchData} />
        </DashModal>
      )}

      {props.editFeatures && (
        <DashModal
          title="Edit Features"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditFeatures fetchData={fetchData} />
        </DashModal>
      )}

      {props.editTitle && (
        <DashModal
          title="Edit Title"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditTitle fetchData={fetchData} />
        </DashModal>
      )}

      {props.editShortDescription && (
        <DashModal
          title="Edit Short Description"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditShortDescription fetchData={fetchData} />
        </DashModal>
      )}

      {props.editLongDescription && (
        <DashModal
          title="Edit Long Description"
          width={1200}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditLongDescription fetchData={fetchData} />
        </DashModal>
      )}

      {props.editQAndA && (
        <DashModal
          title="Edit Questions & Answers"
          width={800}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditQAndAForm fetchData={fetchData} />
        </DashModal>
      )}

      {props.editImage && (
        <DashModal
          title="Edit Image"
          width={600}
          cancleLink={`/betting-websites?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditBettingWebsiteImage fetchData={fetchData} />
        </DashModal>
      )}

      {props.edit && (
        <Drawer
          title="Edit Betting Website"
          onClose={() =>
            router(`/betting-websites?page=${page + 1}&pageSize=${pageSize}`)
          }
          open={true}
        >
          <GeneralEdit page={page} pageSize={pageSize} />
        </Drawer>
      )}
    </div>
  );
};

export default BettingWebsites;
